"use client";

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { DSAppearanceStorageKey } from "@/components/design-system/foundations/appearance";

/**
 * ArtistI18n — minimal i18n layer mirroring the iOS `.xcstrings` flow.
 *
 * Why not next-intl: the Web app is a single-locale shell that swaps
 * dictionaries at runtime (mirroring `Bundle.main.preferredLocalizations`),
 * and adding the next-intl dependency just for ~30 strings would be heavier
 * than the in-app switcher needs. This module keeps the public shape small:
 * `useTranslation()` returns `t(key)` plus the active `locale` and `setLocale`.
 *
 * Locale persistence mirrors `DSAppearanceMode` — stored under
 * `artist.ds.locale` in localStorage, defaulting to `system` (resolved
 * against `navigator.language`).
 */

export type ArtistLocale =
	| "system"
	| "zh-Hans" | "zh-Hant" | "am" | "ar" | "bn" | "de" | "en"
	| "es-419" | "fa" | "fil" | "fr" | "gu" | "ha" | "hi"
	| "id" | "it" | "ja" | "ko" | "mr" | "pt-BR" | "ru"
	| "sw" | "ta" | "te" | "th" | "tr" | "ur" | "vi";

export const ArtistLocaleAll: readonly ArtistLocale[] = [
	"system",
	"zh-Hans", "zh-Hant", "am", "ar", "bn", "de", "en",
	"es-419", "fa", "fil", "fr", "gu", "ha", "hi",
	"id", "it", "ja", "ko", "mr", "pt-BR", "ru",
	"sw", "ta", "te", "th", "tr", "ur", "vi",
];

/** Locales with complete Web dictionaries. Unsupported native locales are
 * still accepted from older persisted preferences but are not offered in UI. */
export const ArtistSelectableLocaleAll: readonly ArtistLocale[] = [
	"system",
	"zh-Hans",
	"en",
];

export const ArtistLocaleStorageKey = "artist.ds.locale";
export const ArtistLocaleCookieKey = "artist-locale";

/** Any locale that can be *resolved* to a dictionary lookup (excludes "system"). */
type ResolvedLocale = Exclude<ArtistLocale, "system">;

function readSystemLocale(): ResolvedLocale {
	if (typeof navigator === "undefined") return "zh-Hans";
	const lang = navigator.language ?? "zh";
	const lower = lang.toLowerCase();
	const resolvedLocales = ArtistLocaleAll.filter(
		(l): l is ResolvedLocale => l !== "system",
	);
	// Exact match (e.g. "pt-BR" → "pt-BR", "zh-Hant" → "zh-Hant")
	const exact = resolvedLocales.find((l) => l.toLowerCase() === lower);
	if (exact) return exact;
	// Prefix match (e.g. "ja-JP" → "ja"); zh* is handled separately below.
	const prefix = lower.split("-")[0];
	const prefixMatch = resolvedLocales
		.filter((l) => l !== "zh-Hans" && l !== "zh-Hant")
		.find((l) => l.toLowerCase() === prefix);
	if (prefixMatch) return prefixMatch;
	// Chinese variants: detect Traditional vs Simplified.
	if (lower.startsWith("zh")) {
		return lower.includes("hant") ||
			lower.includes("tw") ||
			lower.includes("hk") ||
			lower.includes("mo")
			? "zh-Hant"
			: "zh-Hans";
	}
	return "zh-Hans";
}

/**
 * Read the locale from a cookie header — usable on the server (SSR / edge
 * middleware) where `document` is unavailable.  Pass the raw `Cookie`
 * header value (or `null`).
 */
export function readLocaleFromCookie(
	cookieHeader: string | null | undefined,
): ArtistLocale | null {
	if (!cookieHeader) return null;
	const prefix = `${ArtistLocaleCookieKey}=`;
	const match = cookieHeader
		.split(";")
		.map((c) => c.trim())
		.find((c) => c.startsWith(prefix));
	if (!match) return null;
	const raw = decodeURIComponent(match.slice(prefix.length));
	if ((ArtistLocaleAll as readonly string[]).includes(raw)) {
		return raw as ArtistLocale;
	}
	return null;
}

function readLocaleCookieClient(): ArtistLocale | null {
	if (typeof document === "undefined") return null;
	return readLocaleFromCookie(document.cookie);
}

function writeLocaleCookie(locale: ArtistLocale): void {
	if (typeof document === "undefined") return;
	try {
		const maxAge = 60 * 60 * 24 * 365; // 1 year
		document.cookie = `${ArtistLocaleCookieKey}=${encodeURIComponent(locale)}; path=/; max-age=${maxAge}; samesite=lax`;
	} catch {
		/* document.cookie unavailable — skip. */
	}
}

function readStoredLocale(defaultLocale: ArtistLocale = "system"): ArtistLocale {
	if (typeof window === "undefined") return defaultLocale;
	// Prefer localStorage (set by the in-app switcher), then fall back to
	// the cookie (written in parallel so SSR / edge can read it too).
	const raw = window.localStorage?.getItem(ArtistLocaleStorageKey);
	if (raw !== null && (ArtistLocaleAll as readonly string[]).includes(raw)) {
		return raw as ArtistLocale;
	}
	const fromCookie = readLocaleCookieClient();
	if (fromCookie !== null) return fromCookie;
	return defaultLocale;
}

/** Dictionary shape: `Record<locale, Record<key, string>>`. Only `zh-Hans`
 * and `en` are populated today; other locales fall back to `zh-Hans` in `t`.
 * The `satisfies` clause keeps a compile-time shape check without widening
 * the inferred key set (so `keyof typeof DICTIONARY["zh-Hans"]` stays narrow). */
const DICTIONARY = {
	"zh-Hans": {
		// Tab bar (iOS Tabs.xcstrings)
		"tabs.home": "首页",
		"tabs.play": "播放",
		"tabs.create": "创作",
		"tabs.generate": "生成",
		"tabs.me": "我",

		// Make menu (iOS MakeMenu)
		"make.title": "从哪里开始？",
		"make.composeSong": "创作歌曲",
		"make.composeSong.subtitle": "打开创作工作室，生成你的下一首作品",
		"make.camera": "拍照",
		"make.camera.subtitle": "用相机记录眼前的灵感素材",
		"make.writeNote": "写笔记",
		"make.writeNote.subtitle": "在明确标注的离线目录中保存灵感",
		"make.cancel": "取消",
		"make.close": "关闭创作菜单",
		"make.eyebrow": "创作",
		"make.continents": "大陆",

		// Settings sections (iOS SettingsView)
		"settings.title": "设置",
		"settings.section.account": "账户",
		"settings.section.general": "通用",
		"settings.section.make": "创作",
		"settings.section.about": "关于",
		"settings.section.feedbackPrivacy": "反馈与隐私",
		"settings.section.logout": "退出登录",

		"settings.account.editProfile": "编辑资料",
		"settings.account.login": "登录",
		"settings.account.membership": "会员",
		"settings.account.connectedAccounts": "已连接账户",
		"settings.account.homeFeed": "首页信息流",

		"settings.general.appearance": "外观",
		"settings.general.language": "App 语言",
		"settings.general.player": "播放器",
		"settings.general.audioQuality": "音频质量",
		"settings.general.permissions": "系统权限",
		"settings.general.devicePlayback": "设备播放",
		"settings.feedback.open": "发送反馈",
		"settings.feedback.quickEntry": "常驻反馈入口",
		"settings.feedback.analytics": "分析与产品改进",

		"settings.appearance.system": "跟随系统",
		"settings.appearance.light": "浅色",
		"settings.appearance.dark": "深色",

		"settings.language.system": "系统",
		"settings.language.zh-Hans": "简体中文",
		"settings.language.en": "English",

		"settings.feedType.default": "默认",
		"settings.feedType.forYou": "为你推荐",
		"settings.makeWorkTab.studio": "工作室",
		"settings.makeWorkTab.feed": "信息流",
		"settings.displayFilter.allWorks": "全部作品",
		"settings.displayFilter.pinned": "已置顶",
		"settings.videoSound.allowSound": "允许声音",
		"settings.videoSound.muteSound": "默认静音",
		"settings.audioQuality.auto": "自动",
		"settings.audioQuality.high": "高质量",
		"settings.audioQuality.lossless": "无损",
		"settings.audioQuality.hiRes": "Hi-Res 无损",
		"settings.makeWorkTab.title": "Make 工作标签",
		"settings.displayFilter.title": "显示过滤器",
		"settings.videoSound.title": "视频声音",
		"settings.paidGenerateConfirm.title": "付费生成二次确认",
		"settings.player.title": "播放器设置",
		"settings.player.preview": "播放器拨杆",
		"settings.player.description": "选择播放器手动切歌拨杆使用的形状。",
		"settings.player.style": "拨杆滑块样式",
		"settings.player.circle": "圆形",
		"settings.player.diamond": "菱形",
		"settings.player.music": "音符",
		"settings.player.sparkles": "闪光",
		"settings.player.star": "星形",
		"settings.player.emoji": "表情",
		"settings.player.emojiLabel": "表情滑块",
		"settings.audio.title": "音频质量",
		"settings.audio.auto": "流量节省",
		"settings.audio.autoDescription": "AAC 128kbps，启动最快",
		"settings.audio.high": "高质量（推荐）",
		"settings.audio.highDescription": "VBR 256–320kbps",
		"settings.audio.lossless": "无损",
		"settings.audio.losslessDescription": "FLAC，最高 16-bit/48 kHz",
		"settings.audio.hiRes": "Hi-Res 无损",
		"settings.audio.hiResDescription": "FLAC，最高 24-bit/192 kHz",
		"settings.audio.note": "只有源文件的采样率或位深满足要求时才会标记为 Hi-Res，否则按无损显示。",
		"settings.permissions.title": "浏览器权限",
		"settings.permissions.microphone": "麦克风",
		"settings.permissions.camera": "摄像头",
		"settings.permissions.notifications": "通知",
		"settings.permissions.request": "请求权限",
		"settings.permissions.browserNote": "这些权限由当前浏览器和站点控制；拒绝后可在浏览器的网站设置中重新开启。",
		"settings.permissions.state.idle": "尚未请求",
		"settings.permissions.state.granted": "已允许",
		"settings.permissions.state.denied": "已拒绝",
		"settings.permissions.state.default": "尚未决定",
		"settings.permissions.state.unsupported": "浏览器不支持",
		"settings.devicePlayback.title": "设备播放",
		"settings.devicePlayback.browser": "浏览器媒体控制",
		"settings.devicePlayback.supported": "此浏览器支持系统媒体会话和锁屏播放控制。",
		"settings.devicePlayback.unsupported": "此浏览器不支持系统媒体会话控制。",
		"settings.devicePlayback.noOutputs": "浏览器尚未公开可用的音频输出设备。",
		"settings.devicePlayback.outputFallback": "音频输出设备",
		"settings.devicePlayback.output": "浏览器输出",
		"settings.devicePlayback.openPlayer": "打开播放器",

		"settings.about.artist": "关于 Artist",
		"settings.about.userAgreement": "用户协议",
		"settings.about.appleEULA": "Apple 标准 EULA",
		"settings.about.privacyPolicy": "隐私政策",
		"settings.about.version": "版本",

		"settings.logout.confirmTitle": "确定退出登录？",
		"settings.logout.confirmMessage": "此设备上的私有页面将立即关闭。",
		"settings.logout.action": "退出登录",
		"settings.logout.confirm": "确认退出",
		"settings.logout.cancel": "取消",
		"settings.logout.inProgress": "正在退出…",
		"settings.login.action": "登录",

		// Auth (iOS AuthView)
		"auth.title.welcome": "欢迎回到 Artist",
		"auth.title.otp": "验证码验证",
		"auth.subtitle.otp": "已发送至 {destination}",
		"auth.subtitle.credential": "登录以继续你的创作旅程",
		"auth.method.email": "邮箱",
		"auth.field.email": "邮箱地址",
		"auth.field.otp": "6 位验证码",
		"auth.send": "发送验证码",
		"auth.resend": "重新发送",
		"auth.verifyAndLogin": "验证并登录",
		"auth.verifyingScope": "正在绑定作用域…",
		"auth.changeMethod": "更换登录方式",
		"auth.cooldown": "{seconds} 秒后可重发",
		"auth.social.apple": "使用 Apple 登录",
		"auth.social.google": "使用 Google 登录",
		"auth.social.divider": "或使用以下方式登录",
		"auth.terms.prefix": "继续即表示你同意我们的",
		"auth.terms.terms": "服务条款",
		"auth.terms.privacy": "隐私政策",
		"auth.terms.sep": "与",
		"auth.error.email.empty": "请输入邮箱地址。",
		"auth.error.email.tooLong": "邮箱地址过长。",
		"auth.error.email.invalidChars": "邮箱包含当前规则不支持的字符。",
		"auth.error.email.default": "邮箱格式未通过锁定规则。",
		"auth.error.otp.length": "请输入 6 位验证码。",
		"auth.error.scope": "会话未能绑定到当前账户，请重新登录。",
		"auth.error.kernel": "认证能力已安全关闭",
		"auth.error.appleSdk":
			"Apple 登录尚未接入 OAuth redirect，平台 SDK 待集成。",
		"auth.error.googleSdk":
			"Google 登录尚未接入 OAuth redirect，平台 SDK 待集成。",
		"auth.retry.restore": "重新检查",

		// Recognition (iOS ShazamView)
		"recognition.title": "音乐识别",
		"recognition.listening": "正在聆听",
		"recognition.requesting": "正在请求麦克风…",
		"recognition.matching": "正在查找…",
		"recognition.matched": "已找到匹配",
		"recognition.noMatch": "没有找到匹配",
		"recognition.idle": "听一听，找到这首歌",
		"recognition.subheading.listening": "保持设备靠近音乐，收集一段清晰声音。",
		"recognition.subheading.idle":
			"允许麦克风后，Artist 会分析一小段声音并查找歌曲。",
		"recognition.status.matching": "正在查找歌曲…",
		"recognition.status.noMatch":
			"没有找到匹配歌曲。换一段更清晰的音乐再试一次。",
		"recognition.error.generic": "暂时无法完成识别，请稍后重试。",
		"recognition.error.micDenied": "请允许麦克风访问后再开始识别。",
		"recognition.error.unsupported": "此浏览器不支持麦克风录音，无法识别。",
		"recognition.error.micStart": "无法启动录音，请检查麦克风后重试。",
		"recognition.action.start": "开始识别",
		"recognition.action.stop": "停止并识别",
		"recognition.action.retry": "再次识别",
		"recognition.action.requesting": "正在请求…",
		"recognition.action.recognizing": "正在识别…",
		"recognition.history.title": "识别历史",
		"recognition.history.clear": "清空",
		"recognition.history.menu.search": "在 Artist 中搜索",
		"recognition.history.menu.openExternal": "打开外部链接",
		"recognition.history.menu.delete": "删除记录",
		"recognition.history.menu.close": "关闭",
		"recognition.history.menu.title": "更多操作",
		"recognition.match.openInAppleMusic": "在 Apple Music 中打开",
		"recognition.match.searchInArtist": "在 Artist 中搜索",
		"recognition.time.justNow": "刚刚",
		"recognition.time.minutesAgo": "{minutes} 分钟前",
		"recognition.time.hoursAgo": "{hours} 小时前",
		"recognition.time.daysAgo": "{days} 天前",

		// Discovery
		"discovery.tabs.discover": "发现",
		"discovery.tabs.inspiration": "灵感",
		"discovery.tabs.catalog": "目录",
		"discovery.notice.refreshing": "正在刷新…",
		"discovery.error.loadFailed": "发现仓库暂时无法读取。",
		"discovery.empty.inspirations": "暂时没有灵感",
		"discovery.empty.inspirationsMessage": "稍后刷新重试。",
		"discovery.empty.discover": "暂无发现内容",
		"discovery.empty.discoverMessage":
			"仓库已成功响应，但这次没有可展示的目录或账户作品。",
		"discovery.account": "账户与快捷菜单",
		"discovery.search": "搜索",
		"discovery.error.title": "无法打开发现页",
		"discovery.playbackBlocked.title": "播放能力保持关闭",
		"discovery.playbackBlocked.message": "这首公开目录作品没有经过媒体授权验证，未进入播放器。",
		"discovery.hottest.title": "最热门的十首歌",
		"discovery.hottest.subtitle": "Artist 编辑精选",
		"discovery.circles.title": "热门圈子",
		"discovery.circles.playlists": "进入歌单",
		"discovery.circles.trending": "热门曲目",
		"discovery.recent.title": "上次听到",
		"discovery.recent.subtitle": "当前账户",
		"discovery.recent.more": "最近播放",
		"discovery.works.title": "欣赏你的作品",
		"discovery.more": "查看更多",
		"discovery.tutorial.title": "完成您的新手任务",
		"discovery.tutorial.message": "完成新手任务，了解如何使用 Artist",
		"discovery.tutorial.dismiss": "关闭新手任务",

		"genart.back": "返回",
		"genart.upgrade": "升级会员",
		"genart.home": "Artist 首页",
		"genart.messages": "消息",
		"genart.viewer360": "360 模型查看器",
		"genart.models": "生成模型",
		"genart.prompt": "生成提示词",
		"genart.simulatedInspo": "{credits} 模拟灵感",
		"genart.loading": "加载中",
		"genart.loadFailed": "加载失败",
		"genart.promptPlaceholder": "描述你想生成的音乐…",
		"genart.generating": "生成中",
		"genart.confirmGenerate": "确认生成 · {price}",
		"genart.generateMusic": "生成音乐",

		// Common
		"common.retry": "重试",
		"common.refresh": "刷新",
		"common.ok": "知道了",
		"common.cancel": "取消",
		"common.close": "关闭",
		"common.back": "返回",
		"common.skipToContent": "跳到主要内容",

		"share.loading": "正在打开共享画布…",
		"share.missing.title": "共享链接不可用",
		"share.missing.message": "链接可能已失效或被撤销。",
		"share.error.title": "暂时无法打开共享画布",
		"share.error.message": "请稍后重试。",
		"share.readOnlyBy": "{name} 分享的只读画布",
		"share.openInArtist": "在 Artist 打开",
		"player.loading": "正在载入音频…",
		"player.missingTitle": "找不到可播放作品",
		"player.missingMessage": "作品可能未公开、已被删除，或暂时不可播放。",
		"player.backToMake": "返回创作",
		"player.nowPlaying": "正在播放",
		"player.previous": "上一首",
		"player.next": "下一首",
		"player.queuePosition": "队列 {current} / {total}",
		"player.share": "分享歌曲",
		"player.shareText": "听听 {artist} 的《{title}》",
		"player.shared": "已打开系统分享",
		"player.linkCopied": "歌曲链接已复制",
		"player.shareFailed": "暂时无法分享这首歌",
		"player.edit": "编辑作品",
		"player.backToDiscover": "返回发现",
		"invite.title": "加入协作画布",
		"invite.message": "这个链接会授予一个可编辑成员席位。邀请码只保存在地址片段中，不会进入网页服务器日志。",
		"invite.missing": "邀请口令缺失。请让项目所有者重新发送完整链接。",
		"invite.open": "在 Artist 中安全加入",
		"invite.back": "返回 Artist 首页",
		"invite.expiry": "邀请默认 7 天过期、仅可使用一次，项目最多保留 9 个非所有者席位。",
		"legal.privacy.eyebrow": "隐私",
		"legal.privacy.title": "隐私政策",
		"legal.privacy.body1": "艺术家仅处理提供账户、创作、播放、协作与社区功能所需的信息。登录凭据由认证服务安全处理；本地草稿与媒体索引按当前账户作用域隔离保存。",
		"legal.privacy.body2": "公开发布的作品、评论与个人资料会按你的操作展示给其他用户。你可以在账户与设置页面管理登录状态、本地内容和相关权限。",
		"legal.terms.eyebrow": "法律",
		"legal.terms.title": "服务条款",
		"legal.terms.body1": "使用艺术家即表示你同意仅上传或生成自己有权使用的内容，并遵守适用法律、社区规则与平台政策。不得利用服务侵犯他人权利、冒充他人或传播违法有害内容。",
		"legal.terms.body2": "需要账户、生成或支付能力的操作仅在对应服务可用并完成必要验证后执行。价格、额度与权益以提交操作前页面显示的信息为准。",

		// Promotion (iOS PromotionTaskView + LaunchPromotionAdView)
		"promotion.title": "灵感任务",
		"promotion.openAd": "打开免费 Suno 灵感任务",
		"promotion.tasks": "任务",
		"promotion.payback": "灵感返还",
		"promotion.hero.title": "免费创作，从今天开始",
		"promotion.hero.subtitle": "完成任务获得 Suno 灵感与会员消费返还。",
		"promotion.completed": "项已完成",
		"promotion.claim": "领取",
		"promotion.claimCredits": "领取 {credits} 灵感",
		"promotion.signInStart": "登录并开始任务",
		"promotion.daily.title": "今日金币",
		"promotion.daily.wallet": "钱包 {wallet}/{target} 金币 · 满额兑换 {credits} 灵感",
		"promotion.daily.claimed": "今日已领取",
		"promotion.daily.claim": "领取今日金币",
		"promotion.payback.signIn": "登录查看灵感返还",
		"promotion.payback.signInBody": "会员灵感消费里程碑和可领取返还会显示在这里。",
		"promotion.payback.explanationTitle": "消费越多，返还越多",
		"promotion.payback.explanation": "符合条件的套餐按已验证消费里程碑返还灵感。未领取权益 90 天过期，领取后的礼赠灵感 14 天有效。",
		"promotion.payback.offer": "消费 {threshold}% · 返还 {credits} 灵感",
		"promotion.loadFailed": "无法载入灵感任务。",
		"promotion.claimSucceeded": "领取成功",
		"promotion.authRequired": "需要先登录真实账户才能领取。",
	},
	en: {
		"tabs.home": "Home",
		"tabs.play": "Play",
		"tabs.create": "Create",
		"tabs.generate": "Generate",
		"tabs.me": "Me",

		"make.title": "Where to start?",
		"make.composeSong": "Compose song",
		"make.composeSong.subtitle": "Open the studio and generate your next track",
		"make.camera": "Take photo",
		"make.camera.subtitle": "Capture inspiration in front of you",
		"make.writeNote": "Write note",
		"make.writeNote.subtitle": "Save an idea to your offline notes",
		"make.cancel": "Cancel",
		"make.close": "Close create menu",
		"make.eyebrow": "Create",
		"make.continents": "Continents",

		"settings.title": "Settings",
		"settings.section.account": "Account",
		"settings.section.general": "General",
		"settings.section.make": "Make",
		"settings.section.about": "About",
		"settings.section.feedbackPrivacy": "Feedback & Privacy",
		"settings.section.logout": "Sign out",

		"settings.account.editProfile": "Edit profile",
		"settings.account.login": "Sign in",
		"settings.account.membership": "Membership",
		"settings.account.connectedAccounts": "Connected accounts",
		"settings.account.homeFeed": "Home feed",

		"settings.general.appearance": "Appearance",
		"settings.general.language": "App language",
		"settings.general.player": "Player",
		"settings.general.audioQuality": "Audio quality",
		"settings.general.permissions": "System permissions",
		"settings.general.devicePlayback": "Device playback",
		"settings.feedback.open": "Send Feedback",
		"settings.feedback.quickEntry": "Persistent Feedback Entry",
		"settings.feedback.analytics": "Analytics & Improvements",

		"settings.appearance.system": "System",
		"settings.appearance.light": "Light",
		"settings.appearance.dark": "Dark",

		"settings.language.system": "System",
		"settings.language.zh-Hans": "Simplified Chinese",
		"settings.language.en": "English",

		"settings.feedType.default": "Default",
		"settings.feedType.forYou": "For you",
		"settings.makeWorkTab.studio": "Studio",
		"settings.makeWorkTab.feed": "Feed",
		"settings.displayFilter.allWorks": "All works",
		"settings.displayFilter.pinned": "Pinned",
		"settings.videoSound.allowSound": "Allow sound",
		"settings.videoSound.muteSound": "Mute by default",
		"settings.audioQuality.auto": "Auto",
		"settings.audioQuality.high": "High",
		"settings.audioQuality.lossless": "Lossless",
		"settings.audioQuality.hiRes": "Hi-Res lossless",
		"settings.makeWorkTab.title": "Make work tab",
		"settings.displayFilter.title": "Display filter",
		"settings.videoSound.title": "Video sound",
		"settings.paidGenerateConfirm.title": "Confirm paid generation",
		"settings.player.title": "Player settings",
		"settings.player.preview": "Player lever",
		"settings.player.description": "Choose the shape used by the manual track-switch lever.",
		"settings.player.style": "Lever thumb style",
		"settings.player.circle": "Circle",
		"settings.player.diamond": "Diamond",
		"settings.player.music": "Music note",
		"settings.player.sparkles": "Sparkles",
		"settings.player.star": "Star",
		"settings.player.emoji": "Emoji",
		"settings.player.emojiLabel": "Emoji thumb",
		"settings.audio.title": "Audio quality",
		"settings.audio.auto": "Data saver",
		"settings.audio.autoDescription": "AAC 128kbps, fastest startup",
		"settings.audio.high": "High quality (recommended)",
		"settings.audio.highDescription": "VBR 256–320kbps",
		"settings.audio.lossless": "Lossless",
		"settings.audio.losslessDescription": "FLAC up to 16-bit/48 kHz",
		"settings.audio.hiRes": "Hi-Res lossless",
		"settings.audio.hiResDescription": "FLAC up to 24-bit/192 kHz",
		"settings.audio.note": "Hi-Res is shown only when the source sample rate or bit depth qualifies; otherwise it is displayed as lossless.",
		"settings.permissions.title": "Browser permissions",
		"settings.permissions.microphone": "Microphone",
		"settings.permissions.camera": "Camera",
		"settings.permissions.notifications": "Notifications",
		"settings.permissions.request": "Request access",
		"settings.permissions.browserNote": "These permissions are controlled by this browser and site. Re-enable denied access from the browser's site settings.",
		"settings.permissions.state.idle": "Not requested",
		"settings.permissions.state.granted": "Allowed",
		"settings.permissions.state.denied": "Denied",
		"settings.permissions.state.default": "Not decided",
		"settings.permissions.state.unsupported": "Unsupported",
		"settings.devicePlayback.title": "Device playback",
		"settings.devicePlayback.browser": "Browser media controls",
		"settings.devicePlayback.supported": "This browser supports system media sessions and lock-screen playback controls.",
		"settings.devicePlayback.unsupported": "This browser does not support system media-session controls.",
		"settings.devicePlayback.noOutputs": "The browser has not exposed any audio output devices yet.",
		"settings.devicePlayback.outputFallback": "Audio output device",
		"settings.devicePlayback.output": "Browser output",
		"settings.devicePlayback.openPlayer": "Open player",

		"settings.about.artist": "About Artist",
		"settings.about.userAgreement": "User agreement",
		"settings.about.appleEULA": "Apple Standard EULA",
		"settings.about.privacyPolicy": "Privacy policy",
		"settings.about.version": "Version",

		"settings.logout.confirmTitle": "Sign out?",
		"settings.logout.confirmMessage":
			"Private pages on this device will close immediately.",
		"settings.logout.action": "Sign out",
		"settings.logout.confirm": "Confirm sign out",
		"settings.logout.cancel": "Cancel",
		"settings.logout.inProgress": "Signing out…",
		"settings.login.action": "Sign in",

		"auth.title.welcome": "Welcome back to Artist",
		"auth.title.otp": "OTP verification",
		"auth.subtitle.otp": "Sent to {destination}",
		"auth.subtitle.credential": "Sign in to continue your creative journey",
		"auth.method.email": "Email",
		"auth.field.email": "Email address",
		"auth.field.otp": "6-digit code",
		"auth.send": "Send code",
		"auth.resend": "Resend",
		"auth.verifyAndLogin": "Verify and sign in",
		"auth.verifyingScope": "Binding scope…",
		"auth.changeMethod": "Change method",
		"auth.cooldown": "Resend in {seconds}s",
		"auth.social.apple": "Sign in with Apple",
		"auth.social.google": "Sign in with Google",
		"auth.social.divider": "or continue with",
		"auth.terms.prefix": "By continuing you agree to our",
		"auth.terms.terms": "Terms of Service",
		"auth.terms.privacy": "Privacy Policy",
		"auth.terms.sep": "and",
		"auth.error.email.empty": "Please enter an email address.",
		"auth.error.email.tooLong": "Email address is too long.",
		"auth.error.email.invalidChars":
			"Email contains characters the current rules don't support.",
		"auth.error.email.default": "Email failed the locked rules.",
		"auth.error.otp.length": "Please enter the 6-digit code.",
		"auth.error.scope": "The session could not bind to this account. Sign in again.",
		"auth.error.kernel": "Auth capability safely disabled",
		"auth.error.appleSdk":
			"Apple OAuth redirect not wired; platform SDK pending.",
		"auth.error.googleSdk":
			"Google OAuth redirect not wired; platform SDK pending.",
		"auth.retry.restore": "Retry",

		"recognition.title": "Music recognition",
		"recognition.listening": "Listening",
		"recognition.requesting": "Requesting microphone…",
		"recognition.matching": "Looking up…",
		"recognition.matched": "Match found",
		"recognition.noMatch": "No match found",
		"recognition.idle": "Listen, find this song",
		"recognition.subheading.listening":
			"Keep the device close to the music for a clean capture.",
		"recognition.subheading.idle":
			"After you allow the mic, Artist analyses a short clip and looks up the song.",
		"recognition.status.matching": "Looking up the song…",
		"recognition.status.noMatch":
			"No match. Try a clearer snippet and recognise again.",
		"recognition.error.generic":
			"Recognition is temporarily unavailable. Please retry.",
		"recognition.error.micDenied":
			"Please allow microphone access before recognising.",
		"recognition.error.unsupported":
			"This browser doesn't support microphone recording.",
		"recognition.error.micStart":
			"Couldn't start recording. Check the microphone and retry.",
		"recognition.action.start": "Start recognising",
		"recognition.action.stop": "Stop and recognise",
		"recognition.action.retry": "Recognise again",
		"recognition.action.requesting": "Requesting…",
		"recognition.action.recognizing": "Recognising…",
		"recognition.history.title": "Recognition history",
		"recognition.history.clear": "Clear",
		"recognition.history.menu.search": "Search in Artist",
		"recognition.history.menu.openExternal": "Open external link",
		"recognition.history.menu.delete": "Delete record",
		"recognition.history.menu.close": "Close",
		"recognition.history.menu.title": "More actions",
		"recognition.match.openInAppleMusic": "Open in Apple Music",
		"recognition.match.searchInArtist": "Search in Artist",
		"recognition.time.justNow": "just now",
		"recognition.time.minutesAgo": "{minutes} min ago",
		"recognition.time.hoursAgo": "{hours} h ago",
		"recognition.time.daysAgo": "{days} d ago",

		"discovery.tabs.discover": "Discover",
		"discovery.tabs.inspiration": "Inspiration",
		"discovery.tabs.catalog": "Catalog",
		"discovery.notice.refreshing": "Refreshing…",
		"discovery.error.loadFailed":
			"Discovery repository is temporarily unavailable.",
		"discovery.empty.inspirations": "No inspirations yet",
		"discovery.empty.inspirationsMessage": "Pull to refresh later.",
		"discovery.empty.discover": "Nothing to discover yet",
		"discovery.empty.discoverMessage":
			"Repository responded but returned no catalog or account works.",
		"discovery.account": "Account and quick menu",
		"discovery.search": "Search",
		"discovery.error.title": "Couldn’t open Discover",
		"discovery.playbackBlocked.title": "Playback unavailable",
		"discovery.playbackBlocked.message": "This public catalog track has not passed media authorization and was not opened in the player.",
		"discovery.hottest.title": "Hottest Chart",
		"discovery.hottest.subtitle": "Artist editorial picks",
		"discovery.circles.title": "Popular circles",
		"discovery.circles.playlists": "Open playlists",
		"discovery.circles.trending": "Trending tracks",
		"discovery.recent.title": "Last listened",
		"discovery.recent.subtitle": "Current account",
		"discovery.recent.more": "Recently played",
		"discovery.works.title": "Enjoy your work",
		"discovery.more": "See more",
		"discovery.tutorial.title": "Complete your beginner task",
		"discovery.tutorial.message": "Complete the tutorial and learn how to use Artist",
		"discovery.tutorial.dismiss": "Dismiss beginner task",

		"genart.back": "Back",
		"genart.upgrade": "Upgrade",
		"genart.home": "Artist Home",
		"genart.messages": "Messages",
		"genart.viewer360": "360 model viewer",
		"genart.models": "Generation models",
		"genart.prompt": "Generation prompt",
		"genart.simulatedInspo": "{credits} simulated inspo",
		"genart.loading": "Loading",
		"genart.loadFailed": "Couldn’t load",
		"genart.promptPlaceholder": "Describe the music you want to generate…",
		"genart.generating": "Generating",
		"genart.confirmGenerate": "Confirm generation · {price}",
		"genart.generateMusic": "Generate music",

		"common.retry": "Retry",
		"common.refresh": "Refresh",
		"common.ok": "Got it",
		"common.cancel": "Cancel",
		"common.close": "Close",
		"common.back": "Back",
		"common.skipToContent": "Skip to main content",

		"share.loading": "Opening shared canvas…",
		"share.missing.title": "Share link unavailable",
		"share.missing.message": "The link may have expired or been revoked.",
		"share.error.title": "Couldn’t open the shared canvas",
		"share.error.message": "Try again later.",
		"share.readOnlyBy": "Read-only canvas shared by {name}",
		"share.openInArtist": "Open in Artist",
		"player.loading": "Loading audio…",
		"player.missingTitle": "Track unavailable",
		"player.missingMessage": "The track may be private, deleted, or temporarily unavailable.",
		"player.backToMake": "Back to Make",
		"player.nowPlaying": "Now playing",
		"player.previous": "Previous track",
		"player.next": "Next track",
		"player.queuePosition": "Queue {current} / {total}",
		"player.share": "Share track",
		"player.shareText": "Listen to {title} by {artist}",
		"player.shared": "System share opened",
		"player.linkCopied": "Track link copied",
		"player.shareFailed": "This track can’t be shared right now",
		"player.edit": "Edit track",
		"player.backToDiscover": "Back to Discover",
		"invite.title": "Join collaborative canvas",
		"invite.message": "This link grants one editor seat. Its passcode stays in the URL fragment and is never sent to the web server logs.",
		"invite.missing": "The invite passcode is missing. Ask the project owner to send the complete link again.",
		"invite.open": "Join securely in Artist",
		"invite.back": "Back to Artist Home",
		"invite.expiry": "Invites expire after 7 days by default, can be used once, and each project supports up to 9 non-owner seats.",
		"legal.privacy.eyebrow": "Privacy",
		"legal.privacy.title": "Privacy Policy",
		"legal.privacy.body1": "Artist processes only the information required for account, creation, playback, collaboration, and community features. Authentication credentials are handled securely, while local drafts and media indexes remain isolated to the current account scope.",
		"legal.privacy.body2": "Works, comments, and profiles you publish are shown to others according to your actions. You can manage sign-in state, local content, and related permissions from Account and Settings.",
		"legal.terms.eyebrow": "Legal",
		"legal.terms.title": "Terms of Service",
		"legal.terms.body1": "By using Artist, you agree to upload or generate only content you have the right to use and to follow applicable laws, community rules, and platform policies. You may not infringe others’ rights, impersonate others, or distribute illegal harmful content.",
		"legal.terms.body2": "Actions requiring an account, generation, or payment run only when the relevant service is available and required verification is complete. Prices, balances, and benefits are those shown before submission.",

		"promotion.title": "Inspiration Tasks",
		"promotion.openAd": "Open free Suno inspiration tasks",
		"promotion.tasks": "Tasks",
		"promotion.payback": "Inspiration Payback",
		"promotion.hero.title": "Start creating for free today",
		"promotion.hero.subtitle": "Complete tasks to earn Suno inspiration and membership-spend payback.",
		"promotion.completed": "completed",
		"promotion.claim": "Claim",
		"promotion.claimCredits": "Claim {credits} inspiration",
		"promotion.signInStart": "Sign in and start",
		"promotion.daily.title": "Today’s coins",
		"promotion.daily.wallet": "Wallet {wallet}/{target} coins · redeem for {credits} inspiration",
		"promotion.daily.claimed": "Claimed today",
		"promotion.daily.claim": "Claim today’s coins",
		"promotion.payback.signIn": "Sign in to view inspiration payback",
		"promotion.payback.signInBody": "Membership inspiration milestones and claimable payback appear here.",
		"promotion.payback.explanationTitle": "Spend more, receive more back",
		"promotion.payback.explanation": "Eligible plans return inspiration at verified consumption milestones. Unclaimed benefits expire after 90 days; claimed gift inspiration remains valid for 14 days.",
		"promotion.payback.offer": "Spend {threshold}% · receive {credits} inspiration",
		"promotion.loadFailed": "Couldn’t load inspiration tasks.",
		"promotion.claimSucceeded": "Claimed successfully",
		"promotion.authRequired": "Sign in with a real account before claiming.",
	},
} satisfies Partial<Record<ResolvedLocale, Readonly<Record<string, string>>>>;

/** Union of every dictionary key (derived from the `zh-Hans` source of truth). */
type DictionaryKey = keyof typeof DICTIONARY["zh-Hans"];

/** Exported key union so call sites can constrain their `t(key)` arguments. */
export type TranslationKey = DictionaryKey;

interface I18nContextValue {
	readonly locale: ArtistLocale;
	readonly resolvedLocale: ResolvedLocale;
	readonly setLocale: (next: ArtistLocale) => void;
	readonly t: (key: DictionaryKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(
	template: string,
	params?: Record<string, string | number>,
): string {
	if (params === undefined) return template;
	return template.replace(/\{(\w+)\}/g, (_, name: string) =>
		name in params ? String(params[name]) : `{${name}}`,
	);
}

export interface ArtistI18nProviderProps {
	readonly children: ReactNode;
	readonly initialLocale?: ArtistLocale;
	readonly defaultLocale?: ArtistLocale;
}

export function ArtistI18nProvider({
	children,
	initialLocale,
	defaultLocale = "system",
}: Readonly<ArtistI18nProviderProps>) {
	const [locale, setLocaleState] = useState<ArtistLocale>(
		() => initialLocale ?? readStoredLocale(defaultLocale),
	);
	const [systemLocale, setSystemLocale] = useState<ResolvedLocale>(() => readSystemLocale());

	useEffect(() => {
		setLocaleState(initialLocale ?? readStoredLocale(defaultLocale));
		setSystemLocale(readSystemLocale());
		// Cross-instance sync: another tab/document writing the locale key
		// propagates here so the resolved locale stays consistent.
		const onStorage = (event: StorageEvent) => {
			if (event.key !== ArtistLocaleStorageKey) return;
			setLocaleState(readStoredLocale(defaultLocale));
		};
		window.addEventListener?.("storage", onStorage);
		return () => window.removeEventListener?.("storage", onStorage);
	}, [defaultLocale, initialLocale]);

	const resolvedLocale: ResolvedLocale =
		locale === "system" ? systemLocale : locale;

	useEffect(() => {
		document.documentElement.lang = resolvedLocale;
		document.documentElement.dir = ["ar", "fa", "ur"].includes(resolvedLocale)
			? "rtl"
			: "ltr";
	}, [resolvedLocale]);

	const setLocale = useCallback((next: ArtistLocale) => {
		setLocaleState(next);
		try {
			window.localStorage?.setItem(ArtistLocaleStorageKey, next);
		} catch {
			/* localStorage unavailable — keep in-memory state only. */
		}
		// Write cookie in parallel so SSR / edge middleware can read the
		// locale on the next request without waiting for client hydration.
		writeLocaleCookie(next);
		// The native `storage` event only fires in *other* documents; dispatch
		// a synthetic one so any sibling hook instances in this document also
		// re-read the stored value.
		try {
			window.dispatchEvent(
				new StorageEvent("storage", {
					key: ArtistLocaleStorageKey,
					newValue: next,
				}),
			);
		} catch {
			/* StorageEvent constructor unavailable — skip cross-instance sync. */
		}
	}, []);

	const t = useCallback(
		(key: DictionaryKey, params?: Record<string, string | number>) => {
			const fallbackLocale = defaultLocale === "zh-Hans" ? "zh-Hans" : "en";
			const dict: Readonly<Record<string, string>> =
				(DICTIONARY as Partial<Record<ResolvedLocale, Readonly<Record<string, string>>>>)[resolvedLocale] ??
				DICTIONARY[fallbackLocale];
			const template = dict[key] ?? DICTIONARY[fallbackLocale][key] ?? key;
			return interpolate(template, params);
		},
		[defaultLocale, resolvedLocale],
	);

	const value = useMemo<I18nContextValue>(
		() => ({ locale, resolvedLocale, setLocale, t }),
		[locale, resolvedLocale, setLocale, t],
	);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18nContextValue {
	const ctx = useContext(I18nContext);
	if (ctx === null) {
		// Fall back to a static zh-Hans translator if used outside provider
		// (e.g. during SSR or in isolated tests). Production callers should
		// wrap with `ArtistI18nProvider`.
		const dict = DICTIONARY["zh-Hans"];
		const fallback: I18nContextValue = {
			locale: "system",
			resolvedLocale: "zh-Hans",
			setLocale: () => {
				if (import.meta.env.DEV) {
					console.warn(
						"ArtistI18nProvider is not mounted; setLocale is a no-op",
					);
				}
			},
			t: (key, params) => interpolate(dict[key] ?? key, params),
		};
		return fallback;
	}
	return ctx;
}

/**
 * Early inline script — runs before hydration to apply the stored locale /
 * appearance to `<html>` so the first paint matches the user's choice.
 * Rendered as a string from `layout.tsx`.
 */
export function buildAppearanceBootScript(): string {
	return `
(function () {
  try {
    var mode = localStorage.getItem(${JSON.stringify(DSAppearanceStorageKey)}) || "system";
    var prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    var scheme = mode === "system" ? (prefersDark ? "dark" : "light") : mode;
    var root = document.documentElement;
    root.dataset.theme = scheme;
    root.dataset.appearance = mode;
    root.style.colorScheme = scheme;
  } catch (e) {}
})();
`.trim();
}

/**
 * Early inline script — runs before hydration to set `document.documentElement.lang`
 * from the stored locale cookie so the first paint's `<html lang>` attribute
 * is correct for screen readers and SEO.  Rendered as a string from `layout.tsx`.
 */
export function buildLocaleBootScript(): string {
	return `
(function () {
  try {
    var key = ${JSON.stringify(ArtistLocaleCookieKey)};
    var cookies = document.cookie ? document.cookie.split(";") : [];
    var locale = "";
    for (var i = 0; i < cookies.length; i++) {
      var c = cookies[i].trim();
      if (c.indexOf(key + "=") === 0) {
        locale = decodeURIComponent(c.slice(key.length + 1));
        break;
      }
    }
    if (locale && locale !== "system") {
      document.documentElement.lang = locale;
    }
  } catch (e) {}
})();
`.trim();
}
